import { useEffect, useReducer, useState } from "react";
import AppContext from ".";
import Modal from "../components/modal";
import ShoppingCart from "../components/shoppingCart";
import {
  readCart,
  stripMaterialTitle,
  trimProductTitle,
} from "../util/helpers";
import { cartReducer, CART_ACTIONS } from "../util/hooks/cart";
import { imperialReducer } from "../util/hooks/dimensions";

const AppContextWrapper = ({ children }) => {
  const [globalModal, setGlobalModal] = useState(false);
  const [globalModalContent, setGlobalModalContent] = useState({
    modalContent: null,
    options: null,
  });
  const [selectedBottomType, setSelectedBottomType] = useState("wood");
  const [selectedBottomColor, setSelectedBottomColor] = useState(null);
  const [orderState, setOrderState] = useState(0);
  const [materials, setMaterials] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [defaultBaseProduct, setDefaultBaseProduct] = useState(null); // determines which option is selected for the insert: SVG only or ready to install
  const [priceUpdate, setPriceUpdate] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [sides, setSides] = useState(0);
  const [largeKnifeBlocks, setLargeKnifeBlocks] = useState({
    quantity: 0,
    price: 0,
  });
  const [smallKnifeBlocks, setSmallKnifeBlocks] = useState({
    quantity: 0,
    price: 0,
  });
  const [spiceRacks, setSpiceRacks] = useState({
    quantity: 0,
    price: 0,
    spiceJarsCount: 0,
  });
  const [bottom, setBottom] = useState(0);
  const [bottomRequired, setBottomRequired] = useState(true);
  const [bottomRequiredSwitchDisabled, setBottomRequiredSwitchDisabled] =
    useState(false);
  const [disableAllTools, setDisableAllTools] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);
  const [currentInsertDimensions, setCurrentInsertDimensions] = useState(null);
  const [dimensionsUpdate, setDimensionsUpdate] = useState(false);
  const [shoppingCartModal, setShoppingCartModal] = useState(false);
  const [cartTotalPrice, setCartTotalPrice] = useState(0);
  const [insertName, setInsertName] = useState("");
  const [editedItem, setEditedItem] = useState(null);
  const isBrowser = typeof window !== "undefined";

  const MAX_HEIGHT_DEFAULT = 9.525;
  const MAX_HEIGHT_BAMBOO = 30.48;
  /*   const MAX_HEIGHT_CEDAR = 13.97; */
  const MIN_HEIGHT = 2.54;
  const MAX_WIDTH_DEPTH = 100;
  const MIN_WIDTH_DEPTH = 10.16;

  // shopping cart
  const [cartState, dispatch] = useReducer(cartReducer, { cart: [] });

  // dimensions
  const [dimensionsState, dispatchDimensions] = useReducer(imperialReducer, {
    width_fraction: { numerator: 0, denominator: 1 },
    height_fraction: { numerator: 0, denominator: 1 },
    depth_fraction: { numerator: 0, denominator: 1 },
    width_integer: 0,
    depth_integer: 0,
    height_integer: 0,
    widthCm: 0,
    heightCm: 0,
    depthCm: 0,
    init_width_fraction: { numerator: 0, denominator: 1 },
    init_height_fraction: { numerator: 0, denominator: 1 },
    init_depth_fraction: { numerator: 0, denominator: 1 },
    init_width_integer: 0,
    init_depth_integer: 0,
    init_height_integer: 0,
    init_widthCm: 0,
    init_heightCm: 0,
    init_depthCm: 0,
  });

  const ORDER_STATES = {
    designer: 0,
    summary: 1,
  };

  useEffect(() => {
    if (editedItem) setInsertName(editedItem.title);
    else setInsertName("");
  }, [editedItem]);

  useEffect(() => {
    // initialize cart state
    (async () => {
      const cart = await readCart();
      dispatch({ type: CART_ACTIONS.INITIALIZE_CART, value: cart });
    })();
    /*     const auto_save_off = localStorage.getItem("auto_save_off");
    if (auto_save_off) setAutoSave(JSON.parse(!auto_save_off)); */
    const show_tooltips = localStorage.getItem("show_tooltips");
    if (show_tooltips) setShowTooltips(JSON.parse(show_tooltips));
  }, []);

  useEffect(() => {
    if (selectedMaterial) {
      const areaObject = window?.calcAreas?.();
      if (areaObject) {
        const bottomMaterials = materials.filter((material) =>
          material.node.tags.includes("bottom-material")
        );
        const smallKnifeBlock = materials.find((material) =>
          material.node.tags.includes("knife-block-base")
        );
        const largeKnifeBlock = materials.find((material) =>
          material.node.tags.includes("large-knife-block-base")
        );
        const spiceRack = materials.find((material) =>
          material.node.tags.includes("spice-rack-base")
        );

        let bottomMaterial = null;
        if (selectedBottomType !== "none" || !bottomRequired)
          bottomMaterial = bottomMaterials.find(
            (material) =>
              trimProductTitle(material.node.title).toLowerCase() ===
              selectedBottomType
          );

        let largeKnifeBlocks = {
          quantity: 0,
          price: 0,
        };
        let smallKnifeBlocks = {
          quantity: 0,
          price: 0,
        };
        let spiceRacks = {
          quantity: 0,
          price: 0,
          spiceJarsCount: 0,
        };
        if (
          largeKnifeBlock &&
          typeof areaObject.hasLargeKnifeBlock === "number"
        ) {
          largeKnifeBlocks = {
            ...largeKnifeBlock.node,
            price: parseFloat(
              largeKnifeBlock.node.priceRange.minVariantPrice.amount
            ),
            quantity: areaObject.hasLargeKnifeBlock,
          };
          setLargeKnifeBlocks(largeKnifeBlocks);
        }
        if (
          smallKnifeBlock &&
          typeof areaObject.hasSmallKnifeBlock === "number"
        ) {
          smallKnifeBlocks = {
            ...smallKnifeBlock.node,
            price: parseFloat(
              smallKnifeBlock.node.priceRange.minVariantPrice.amount
            ),
            quantity: areaObject.hasSmallKnifeBlock,
          };
          setSmallKnifeBlocks(smallKnifeBlocks);
        }
        if (spiceRack && typeof areaObject.hasSpiceRack === "number") {
          spiceRacks = {
            ...spiceRack.node,
            price: parseFloat(spiceRack.node.priceRange.minVariantPrice.amount),
            quantity: areaObject.hasSpiceRack,
          };
          setSpiceRacks(spiceRacks);
        }
        const sides = parseInt(
          parseFloat(areaObject.sideArea) *
            10000 *
            parseFloat(selectedMaterial.priceRange.minVariantPrice.amount)
        );

        let bottomPrice =
          bottomRequired && selectedBottomType !== "none"
            ? parseInt(
                parseFloat(areaObject.baseArea) *
                  10000 *
                  parseFloat(
                    bottomMaterial.node.priceRange.minVariantPrice.amount
                  )
              )
            : 0;

        setCurrentPrice(
          parseFloat(
            defaultBaseProduct.node.priceRange.minVariantPrice.amount
          ) +
            sides +
            bottomPrice +
            largeKnifeBlocks.quantity * largeKnifeBlocks.price +
            smallKnifeBlocks.quantity * smallKnifeBlocks.price +
            spiceRacks.quantity * spiceRacks.price
        );

        setSides({
          area: parseFloat(areaObject.sideArea),
          price: defaultBaseProduct
            ? parseFloat(
                defaultBaseProduct.node.priceRange.minVariantPrice.amount
              ) +
              sides +
              largeKnifeBlocks.price +
              smallKnifeBlocks.price +
              spiceRacks.price
            : sides +
              largeKnifeBlocks.price +
              smallKnifeBlocks.price +
              spiceRacks.price,
          sidesPrice: sides,
          basePrice: defaultBaseProduct
            ? parseFloat(
                defaultBaseProduct.node.priceRange.minVariantPrice.amount
              )
            : 0,
        });
        setBottom({
          area: parseFloat(areaObject.baseArea),
          price: bottomPrice,
          color: selectedBottomColor,
          bottomMaterial,
        });
      }
    }
  }, [bottomRequired, selectedBottomColor, priceUpdate, selectedMaterial]);

  useEffect(() => {
    let sum = 0;
    cartState.cart.forEach((item) => {
      sum += item.quantity * parseFloat(item.price);
    });
    setCartTotalPrice(sum / 100);
  }, [cartState.cart]);

  const handleSetGlobalModal = (modalContent, options) => {
    setGlobalModal(!globalModal);
    setGlobalModalContent({ modalContent, options });
  };

  const handleSetOrderState = (state) => {
    setOrderState(ORDER_STATES[state]);
  };

  const handleSetMaterials = (array) => {
    setMaterials(array);
    if (array.length > 0) {
      const defaultBase = array.find((material) =>
        material.node.tags.includes("insert-base-default")
      );
      setDefaultBaseProduct(defaultBase);
    }
  };

  const handleSetSelectedMaterial = (material) => {
    setSelectedMaterial({
      ...material,
      name: stripMaterialTitle(material.title),
    });
  };

  const triggerPriceUpdate = () => {
    setPriceUpdate(!priceUpdate);
  };

  const handleSetBottomRequired = (bool) => {
    setBottomRequired(bool);
  };

  const handleSetDisableAllTools = (bool) => {
    setDisableAllTools(bool);
  };

  const triggerDimensionsUpdate = () => {
    setDimensionsUpdate(!dimensionsUpdate);
  };

  const handleSetShowTooltips = (bool) => {
    localStorage.setItem("show_tooltips", bool);
    setShowTooltips(bool);
  };

  const handleSetShoppingCartModal = () => {
    setShoppingCartModal(!shoppingCartModal);
  };

  const handleSetCurrentInsertDimensions = (dimensions) => {
    setCurrentInsertDimensions(dimensions);
  };

  const addInsertToCart = (insert) => {
    const insertJson = window?.saveInsert?.(true);
    dispatch({
      type: CART_ACTIONS.ADD_INSERT,
      insert: { ...insert, insertJson },
    });
  };

  const editInsertInCart = (insert) => {
    const insertJson = window?.saveInsert?.(true);
    dispatch({
      type: CART_ACTIONS.EDIT_INSERT,
      insert: { ...insert, insertJson },
    });
  };

  const removeInsertFromCart = (insert) => {
    dispatch({ type: CART_ACTIONS.REMOVE_INSERT, insert });
  };

  const increaseInsertQuantity = (insert, quantity = 1) => {
    dispatch({ type: CART_ACTIONS.INCREASE_QUANTITY, insert, quantity });
  };

  const decreaseInsertQuantity = (insert) => {
    dispatch({ type: CART_ACTIONS.DECREASE_QUANTITY, insert });
  };

  const handleSetAutoSave = (bool) => {
    localStorage.setItem("auto_save_off", bool);
    setAutoSave(bool);
  };

  const disableBottomRequiredSwitch = (bool) => {
    setBottomRequiredSwitchDisabled(bool);
  };

  const handleSetSelectedBottomType = (type, color = null) => {
    const bottomRequiredInput = document.getElementById("bottom-required");
    if (type === "none") bottomRequiredInput.checked = false;
    else bottomRequiredInput.checked = true;
    setSelectedBottomType(type);
    window?.checkBottomRequired();
    if (type === "felt") {
      setTimeout(() => {
        window?.setFeltBase();
        if (color) window?.setFeltColour(color);
      }, 100);
    }
    if (type === "non-slip") {
      setTimeout(() => {
        window?.setAntislipBase();
        if (color) window?.setAntislipColour(color);
      }, 100);
    }
  };

  const handleSetSelectedBottomColor = (color, type) => {
    if (type === "felt" || type === "non-slip") setSelectedBottomColor(color);
    else setSelectedBottomColor(null);
  };

  const handleSetInsertName = (name) => setInsertName(name);

  const context = {
    isBrowser,
    globalModal,
    orderState,
    materials,
    selectedMaterial,
    defaultBaseProduct,
    priceUpdate,
    sides,
    bottom,
    largeKnifeBlocks,
    smallKnifeBlocks,
    spiceRacks,
    bottomRequired,
    disableAllTools,
    showTooltips,
    dimensionsUpdate,
    currentInsertDimensions,
    cart: cartState.cart,
    cartTotalPrice,
    currentPrice,
    autoSave,
    MAX_HEIGHT_BAMBOO,
    /*     MAX_HEIGHT_CEDAR, */
    MAX_HEIGHT_DEFAULT,
    MIN_HEIGHT,
    MIN_WIDTH_DEPTH,
    MAX_WIDTH_DEPTH,
    insertName,
    bottomRequiredSwitchDisabled,
    selectedBottomType,
    editedItem,
    dimensionsState,
    handleSetAutoSave,
    triggerDimensionsUpdate,
    handleSetShowTooltips,
    handleSetGlobalModal,
    handleSetOrderState,
    handleSetMaterials,
    handleSetSelectedMaterial,
    handleSetBottomRequired,
    handleSetDisableAllTools,
    triggerPriceUpdate,
    handleSetShoppingCartModal,
    handleSetCurrentInsertDimensions,
    addInsertToCart,
    editInsertInCart,
    removeInsertFromCart,
    increaseInsertQuantity,
    decreaseInsertQuantity,
    disableBottomRequiredSwitch,
    handleSetSelectedBottomType,
    handleSetSelectedBottomColor,
    handleSetInsertName,
    setEditedItem,
    dispatchDimensions,
  };
  return (
    <AppContext.Provider value={context}>
      <Modal
        trigger={globalModal}
        closeCallback={() => {
          if (globalModalContent?.options?.customCloseCallBack)
            globalModalContent?.options.customCloseCallBack();
          setGlobalModalContent({ modalContent: null, options: null });
        }}
        options={globalModalContent.options}
      >
        {globalModalContent.modalContent}
      </Modal>
      <Modal trigger={shoppingCartModal}>
        <div className="common-modal">
          <ShoppingCart
            handleCloseModal={() => setShoppingCartModal(!shoppingCartModal)}
          />
        </div>
      </Modal>
      {children}
    </AppContext.Provider>
  );
};
export default AppContextWrapper;
